<template>
    <div class="bg-white w-full">

        <div class="group">
            <div class="caption">
                <h5 class="text-base font-medium text-green-dark">Werkzaamheden</h5>
            </div>
            <div class="fields flex flex-col w-full p-6 pb-2">
                <div class="flex flex-wrap w-full md:w-2/3 mx-0 md:-mx-2">
                    <field-group :isAllowed="hasPermission"
                        :value="$moment(order.reporting_finished_at_the_latest).format('DD-MM-YYYY')"
                        label="Rapportage gereed" class="w-full md:w-1/2 md:px-2 mb-4">
                        <datepicker id="rapportage-gereed" placeholder="dd-mm-yyyy" calendar-class="datepicker"
                            :value="reportingFinishedAtTheLatest"
                            @input="dateSelected($event, 'reporting_finished_at_the_latest')" :format="formatter"
                            :language="dutch"></datepicker>
                    </field-group>
                    <field-group :isAllowed="hasPermission"
                        :value="$moment(order.sanitation_finished_at_the_latest).format('DD-MM-YYYY')"
                        label="Sanering gereed" class="w-full md:w-1/2 md:px-2 mb-4" v-if="showSanitationDate">
                        <datepicker id="sanering-gereed" placeholder="dd-mm-yyyy" calendar-class="datepicker"
                            :value="sanitationFinishedAtTheLatest"
                            @input="dateSelected($event, 'sanitation_finished_at_the_latest')" :format="formatter"
                            :language="dutch"></datepicker>
                    </field-group>
                </div>
                <div class="w-full mb-4" v-if="$subject.isAdmin()">
                    <label for="description">Omschrijving</label>
                    <textarea class="textarea" id="description" rows="6" v-model="order.desc"
                        @blur="resize(120, $event)" @focus="resize(120, $event)"></textarea>
                </div>
            </div>
        </div>

        <div class="group">
            <div class="caption flex items-center justify-between">
                <h5 class="text-base font-semibold font-medium text-green-dark">Calamiteiten</h5>
                <button type="button" class="btn btn-sm bg-green-dark" v-if="canAddLimitation"
                    @click="openLimitationModal(null)">Beperking toevoegen</button>
            </div>

            <div class="fields flex flex-col w-full p-6">
                <div class="w-full">
                    <div class="field-group">
                        <div class="field-heading">
                            <label>Beperkingen</label>
                        </div>
                        <div class="flex flex-wrap mx-0 md:-mx-2 -mb-4 mt-4" v-if="hasLimitations">
                            <div class="w-full md:w-1/2 px-0 md:px-2 mb-4" v-for="limitation in inventory.limitations"
                                :key="limitation.id">
                                <div class="border rounded p-4"
                                    :class="{ 'hover:bg-grey-lighter cursor-pointer': hasPermission }"
                                    v-on="hasPermission ? { click: () => openLimitationModal(limitation) } : {}">

                                    <div
                                        class="w-full flex justify-between items-center text-xs text-grey-darkest font-bold uppercase tracking-wide">
                                        <span>{{ limitation.title }}</span>
                                    </div>

                                    <div class="w-full flex flex-col">
                                        <span class="w-full text-xs text-grey-dark mt-1 mr-6">{{ limitation.material
                                        }}</span>
                                        <span class="w-full text-sm text-grey-darkest mt-4">{{ limitation.desc }}</span>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div v-else class="alert alert-info" role="alert">
                            <span class="title">Geen beperkingen aanwezig</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <div class="group">
            <div class="caption">
                <h5 class="text-base font-semibold font-medium text-green-dark">Veldwerk</h5>
            </div>

            <div class="fields flex flex-col w-full p-6">
                <div class="flex flex-wrap -mx-2 mb-4 md:mb-0">
                    <field-group :isAllowed="hasPermission" :value="order.project_number" label="Projectnummer"
                        class="w-1/2 md:w-1/6 px-2">
                        <input class="text" type="text" name="projectnumber" id="projectnumber"
                            placeholder="Voer een projectnummer in" v-model="inventory.order.project_number" />
                    </field-group>

                    <select-input class="w-full md:w-1/3 px-2" label="Objectstatus" v-model="order.status"
                        :options="statuses" :has-permission="hasPermission" :deselect="false"></select-input>

                    <autocomplete-input class="w-full flex-1 px-2" label="Onderzoeker" name="investigator"
                        v-model="inventory.investigator.name" :items="investigators" :isAsync="true"
                        :has-permission="hasPermission" @changed="searchInvestigator"></autocomplete-input>
                </div>

                <div class="w-full">
                    <field-group :isAllowed="hasPermission" :value="inventory.desc" label="Opmerkingen">
                        <textarea class="textarea" id="inventory_desc" rows="3" v-model="inventory.desc"
                            @blur="resize(120, $event)" @focus="resize(120, $event)"></textarea>
                    </field-group>
                </div>
            </div>
        </div>

        <div class="group">
            <div class="caption flex items-center justify-between">
                <h5 class="text-base font-semibold font-medium text-green-dark">Bronnen</h5>
                <button type="button" class="btn btn-sm bg-green-dark" v-if="canAddSource"
                    @click="openAddSourceModal">Bron toevoegen</button>
            </div>

            <div class="fields flex flex-col w-full p-6">
                <div class="flex flex-wrap mx-0 md:-mx-2 -mb-4" v-if="hasAnySources">
                    <template v-for="child in subject.children">
                        <div class="w-full md:w-1/2 lg:w-1/3 px-0 md:px-2 mb-4" v-for="(source, index) in child.sources"
                            :key="source.id">
                            <div class="border rounded p-4"
                                :class="{ 'hover:bg-grey-lighter cursor-pointer': hasPermission }"
                                v-on="hasPermission ? { click: () => showSourceModal(child, index) } : {}">
                                <div
                                    class="w-full flex justify-between items-center text-xs text-grey-darkest font-bold uppercase tracking-wide">
                                    <span>{{ source.name }}</span>
                                </div>

                                <div class="w-full flex justify-between mt-4">
                                    <span class="text-xs text-grey-dark mr-6">Ruimte</span>
                                    <span class="text-sm text-right font-semibold text-grey-darkest">{{ child.name
                                    }}</span>
                                </div>

                                <div class="w-full flex justify-between mt-2">
                                    <span class="text-xs text-grey-dark mr-6">Oppervlakte</span>
                                    <span class="text-sm text-right font-semibold text-grey-darkest" v-if="source.unit">
                                        {{ source.amount }} {{ source.unit.name }}
                                    </span>
                                </div>

                                <div class="w-full flex justify-between mt-2">
                                    <span class="text-xs text-grey-dark mr-6">Methode</span>
                                    <span class="text-sm text-right font-semibold text-grey-darkest"
                                        v-if="source.remove_method">
                                        {{ source.remove_method.name }}
                                    </span>
                                </div>

                                <div class="w-full flex justify-between mt-3">
                                    <span :class="sourceStatusClass(source.status.name)"
                                        class="text-xs w-full text-center font-semibold py-2 rounded">
                                        {{ source.status.name }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div class="w-full md:w-1/2 lg:w-1/3 px-0 md:px-2 mb-4" v-for="source in subject.sources"
                        :key="source.id">
                        <div class="border rounded p-4">
                            <div
                                class="w-full flex justify-between items-center text-xs text-grey-darkest font-bold uppercase tracking-wide">
                                <span>{{ source.name }}</span>
                            </div>

                            <div class="w-full flex justify-between mt-4">
                                <span class="text-xs text-grey-dark mr-6">Ruimte</span>
                                <span class="text-sm text-right font-semibold text-grey-darkest">Op object zelf</span>
                            </div>

                            <div class="w-full flex justify-between mt-2">
                                <span class="text-xs text-grey-dark mr-6">Oppervlakte</span>
                                <span class="text-sm text-right font-semibold text-grey-darkest" v-if="source.unit">
                                    {{ source.amount }} {{ source.unit.name }}
                                </span>
                            </div>

                            <div class="w-full flex justify-between mt-2">
                                <span class="text-xs text-grey-dark mr-6">Methode</span>
                                <span class="text-sm text-right font-semibold text-grey-darkest"
                                    v-if="source.remove_method">
                                    {{ source.remove_method.name }}
                                </span>
                            </div>

                            <div class="w-full flex justify-between mt-3">
                                <span :class="sourceStatusClass(source.status.name)"
                                    class="text-xs w-full text-center font-semibold py-2 rounded">
                                    {{ source.status.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="alert alert-info" role="alert">
                    <span class="title">Geen bronnen aanwezig</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import SubjectPerimeter from '@/perimeters/subject';
import SearchSelect from '@/components/globals/SearchSelect';
import FieldGroup from '@/components/globals/FieldGroup';
import SelectInput from '@/components/inputs/Select';
import InvestigatorService from '@/services/investigator';
import SourceService from '@/services/source';
import SubjectService from '@/services/subject';
import AutocompleteInput from '@/components/inputs/Autocomplete';

import { EventBus } from '@/event-bus';
import { nl } from 'vuejs-datepicker/dist/locale';

export default {
    name: 'TabOrder',
    components: {
        Datepicker,
        SearchSelect,
        FieldGroup,
        AutocompleteInput,
        SelectInput
    },
    props: {
        subject: {
            type: Object,
            required: true
        },
        hasPermission: {
            type: Boolean,
            required: true
        }
    },
    perimeters: [SubjectPerimeter],
    data() {
        return {
            statuses: [],
            investigators: [],
            retryInvestigatorSearch: true,
            inventory: null,
            order: null,
            source: null,
            band: null,
            dutch: nl
        };
    },
    created() {
        EventBus.$on('source-saved', this.changeSource);
        EventBus.$on('source-deleted', this.deletedSource);
        EventBus.$on('sources-added', this.sourcesAdded);
        EventBus.$on('limitation-added', this.limitationAdded);
        EventBus.$on('limitation-saved', this.limitationSaved);
        EventBus.$on('limitation-deleted', this.limitationDeleted);

        this.inventory = this.subject.inventories[
            this.subject.inventories.length - 1
        ];
        this.order = this.inventory.order;

        this.fetchStatuses();
    },
    beforeDestroy() {
        EventBus.$off('source-saved', this.changeSource);
        EventBus.$off('source-deleted', this.deletedSource);
        EventBus.$off('sources-added', this.sourcesAdded);
        EventBus.$off('limitation-added', this.limitationAdded);
        EventBus.$off('limitation-saved', this.limitationSaved);
        EventBus.$off('limitation-deleted', this.limitationDeleted);
    },
    methods: {
        dateSelected(date, property) {
            this.order[property] = this.$moment(date).format('Y-MM-DD HH:mm:ss');
        },
        formatter(date) {
            return this.$moment(date).format('DD-MM-YYYY');
        },
        deletedSource(source) {
            const child = this.subject.children.find(item => {
                return item.sources.some(sourceItem => sourceItem.id === source.id);
            });

            if (!child) return;

            const index = child.sources.findIndex(sourceItem => sourceItem.id === source.id);
            child.sources.splice(index, 1);
        },
        changeSource(room) {
            const index = this.subject.children.findIndex(item => {
                return item.id === room.id;
            });
            this.$set(this.subject.children, index, room);
        },
        async sourcesAdded() {
            try {
                const { data: response } = await SubjectService.get(this.subject.id);
                this.subject.children = response.data.children;
                this.$set(this.subject, 'sources', response.data.sources);
            } catch (error) {
                this.$store.commit('subject/setError', error);
            }
        },
        limitationAdded(limitation) {
            this.inventory.limitations.push(limitation);
        },
        limitationSaved(limitation) {
            const index = this.inventory.limitations.findIndex(item => item.id === limitation.id);
            if (index === -1) return;
            this.$set(this.inventory.limitations, index, limitation);
        },
        limitationDeleted(limitation) {
            const index = this.inventory.limitations.findIndex(item => item.id === limitation.id);
            if (index === -1) return;
            this.inventory.limitations.splice(index, 1);
        },
        openLimitationModal(limitation) {
            this.$store.commit('modal/setItems', {
                inventoryId: this.inventory.id,
                limitation: limitation
            });
            this.$store.commit('modal/show', 'LimitationModal');
        },
        openAddSourceModal() {
            this.$store.commit('modal/setItems', {
                subjectId: this.subject.id,
                rooms: this.subject.children
            });
            this.$store.commit('modal/show', 'AddSourceModal');
        },
        sourceStatusClass(value) {
            return value
                .toLowerCase()
                .replace(/[^\w ]+/g, '')
                .replace(/ +/g, '-');
        },
        resize(min, event) {
            let target = event.target;

            if (event.type === 'blur') {
                return target.style.setProperty('height', 'auto');
            }

            let height = target.scrollHeight + 2;
            if (min && target.clientHeight < target.scrollHeight) {
                height = height < min ? min : height;
            }

            target.style.setProperty('height', height + 'px');
            return;
        },
        showSourceModal(room, index) {
            this.$store.commit('modal/setItems', { room, index });
            this.$store.commit('modal/show', 'SourceModal');
        },
        async fetchStatuses() {
            try {
                const { data: response } = await SourceService.statuses('order');
                this.statuses = response.data;
            } catch (error) {
                this.statuses = null;
                this.error = error;
            }
        },
        async searchInvestigator(value) {
            if (value.trim().length < 3) return (this.retryInvestigatorSearch = true);

            if (!this.investigators.length && !this.retryInvestigatorSearch) {
                return (this.investigators = []);
            }

            const { data: response } = await InvestigatorService.search(value);
            this.investigators = response.data.map(investigator => {
                return investigator.name;
            });

            if (!this.investigators.length) {
                this.retryInvestigatorSearch = false;
            }
        }
    },
    computed: {
        canAddSource() {
            return this.$isAllowed('addSource');
        },
        canAddLimitation() {
            return this.$isAllowed('addLimitation');
        },
        hasLimitations() {
            return this.inventory.limitations && this.inventory.limitations.length > 0;
        },
        hasSources() {
            return this.subject.children && this.subject.children.length > 0;
        },
        hasAnySources() {
            const childSources = this.subject.children && this.subject.children.some(child => {
                return child.sources && child.sources.length;
            });
            const directSources = this.subject.sources && this.subject.sources.length > 0;

            return !!(childSources || directSources);
        },
        showSanitationDate() {
            let statuses = ['Afgerond', 'Asbest - sanering'];
            return statuses.indexOf(this.inventory.order.status.name) > -1;
        },
        reportingFinishedAtTheLatest() {
            if (this.order && this.order.reporting_finished_at_the_latest) {
                return this.$moment(
                    this.order.reporting_finished_at_the_latest
                ).toDate();
            }
        },
        sanitationFinishedAtTheLatest() {
            if (this.order && this.order.sanitation_finished_at_the_latest) {
                return this.$moment(
                    this.order.sanitation_finished_at_the_latest
                ).toDate();
            }
        }
    }
};
</script>
